import { ReportGroup } from '@/api/generatedApi';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMoveAssetsToGroup } from '@/hooks/api/reports/useMoveAssetsToGroup';
import { useUpdateReportGroupName } from '@/hooks/api/reports/useUpdateReportGroupName';
import { useDeleteReportGroup } from '@/hooks/api/reports/useDeleteReportGroup';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { SourceGroupDisplay } from './SourceGroupDisplay';
import { DestinationGroupAutocomplete } from './DestinationGroupAutocomplete';
import { MergedGroupNameSelector } from './MergedGroupNameSelector';
import { useQueryClient } from '@tanstack/react-query';
import { REPORT_GROUPS_QUERY_KEY, useInfiniteReportGroups } from '@/hooks/api/reports/useInfiniteReportGroups';
import { REPORT_GROUP_ASSETS_QUERY_KEY } from '@/hooks/api/reports/useListReportGroupAssets';

type NameOption = 'source' | 'destination' | 'custom';

export const MergeGroupModal = ({
  open,
  onClose,
  onSuccess,
  sourceGroup,
  reportId,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  sourceGroup: ReportGroup;
  reportId: string;
}) => {
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>();
  const [nameOption, setNameOption] = useState<NameOption>('custom');
  const [customName, setCustomName] = useState('');
  const [isNameSectionExpanded, setIsNameSectionExpanded] = useState(false);
  const showToast = useToast(state => state.showToast);
  const navigate = useNavigate();
  const { getPath } = useTenants();
  const queryClient = useQueryClient();

  const { groups, loadAll, isFetchingNextPage } = useInfiniteReportGroups({ id: reportId });

  useEffect(() => {
    if (open) {
      loadAll();
    }
  }, [open, loadAll]);

  const handleMergeComplete = () => {
    showToast({ message: 'Groups merged successfully', type: 'success' });
    onSuccess?.();
    handleModalClose();

    if (selectedGroupId) {
      navigate(getPath(`report/${reportId}/${selectedGroupId}`));
      queryClient.resetQueries({ queryKey: [REPORT_GROUPS_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [REPORT_GROUP_ASSETS_QUERY_KEY], exact: false });
    }
  };

  const { mutate: deleteGroup, isPending: isDeleting } = useDeleteReportGroup({
    reportId,
    groupId: sourceGroup.id,
    onSuccess: handleMergeComplete,
    onError: error => {
      showToast({ message: error, type: 'error' });
    },
  });

  const { mutate: updateGroupName, isPending: isUpdatingName } = useUpdateReportGroupName({
    onSuccess: () => {
      deleteGroup();
    },
    onError: error => {
      showToast({ message: error, type: 'error' });
    },
  });

  const getNewGroupName = (): string | undefined => {
    if (!isNameSectionExpanded) return undefined;
    if (nameOption === 'source') return sourceGroup.name;
    if (nameOption === 'custom') return customName.trim();
    return undefined;
  };

  const { mutate: moveAssets, isPending: isMoving } = useMoveAssetsToGroup({
    reportId,
    onSuccess: () => {
      const newName = getNewGroupName();

      if (selectedGroupId && newName !== undefined) {
        updateGroupName({ reportId, groupId: selectedGroupId, name: newName });
      } else {
        deleteGroup();
      }
    },
    onError: (error: string) => {
      showToast({ message: error, type: 'error' });
    },
  });

  const availableGroups = groups.filter(group => group.id !== sourceGroup.id);
  const sourceGroupIndex = groups.findIndex(g => g.id === sourceGroup.id);
  const sourceGroupName = sourceGroup.name || `Group ${sourceGroupIndex + 1}`;

  const handleMerge = () => {
    if (selectedGroupId) {
      moveAssets({
        assetIds: sourceGroup.assetIds,
        sourceGroupId: sourceGroup.id,
        targetGroupId: selectedGroupId,
      });
    }
  };

  const handleModalClose = () => {
    setSelectedGroupId(undefined);
    setNameOption('custom');
    setCustomName('');
    setIsNameSectionExpanded(false);
    onClose();
  };

  const selectedGroup = availableGroups.find(group => group.id === selectedGroupId);

  const totalAssets = sourceGroup.assetIds.length + (selectedGroup?.assetIds.length || 0);
  const isLoading = isMoving || isUpdatingName || isDeleting;

  const handleGroupSelect = (groupId: string | undefined) => {
    setSelectedGroupId(groupId);
    if (groupId) {
      const selected = availableGroups.find(g => g.id === groupId);
      const hasRealNames = !!(sourceGroup.name || selected?.name);
      setIsNameSectionExpanded(hasRealNames);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth='sm'
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <DialogTitle>Merge Groups</DialogTitle>
      <DialogContent>
        <SourceGroupDisplay group={sourceGroup} groupName={sourceGroupName} />
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Select destination group:
        </Typography>
        <Box sx={{ mb: 3 }}>
          <DestinationGroupAutocomplete
            availableGroups={availableGroups}
            allGroups={groups}
            selectedGroupId={selectedGroupId}
            onGroupSelect={handleGroupSelect}
            isLoadingGroups={isFetchingNextPage}
          />
        </Box>
        {selectedGroupId && (
          <MergedGroupNameSelector
            totalAssets={totalAssets}
            nameOption={nameOption}
            customName={customName}
            sourceGroupName={sourceGroup.name}
            destinationGroupName={selectedGroup?.name}
            expanded={isNameSectionExpanded}
            onNameOptionChange={setNameOption}
            onCustomNameChange={setCustomName}
            onExpandedChange={setIsNameSectionExpanded}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleModalClose} color='primary' disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleMerge}
          variant='contained'
          color='primary'
          disabled={!selectedGroupId || isLoading}
          sx={{
            backgroundColor: 'secondary.main',
            '&:hover': {
              backgroundColor: 'secondary.dark',
            },
            maxWidth: '300px',
            minWidth: 0,
          }}
        >
          {isLoading ? (
            'Merging...'
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
              <span style={{ flexShrink: 0 }}>Merge</span>
            </Box>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
